import { CtxMapV2 } from 'components/mapV2/contexts/CtxMapV2';
import { MapV2LanguageSwitch } from 'components/mapV2/elements/MapV2LanguageSwitch';
import { useActions } from 'components/mapV2/hooks/useActions';
import { useMarkers } from 'components/mapV2/hooks/useMarkers';
import { MapV2Categories } from 'components/mapV2/MapV2Categories';
import { MapV2IframeBar } from 'components/mapV2/MapV2IframeBar';
import { MapV2Modal } from 'components/mapV2/MapV2Modal';
import { useViewMapQueryParams } from 'hooks/map/useViewMapQueryParams';
import { uniq } from 'lodash';
import { CustomMap } from 'pages/custom/shared/CustomMap';
import { useUrlGuard } from 'pages/custom/shared/CustomMapPage/useUrlGuard';
import { S_PageStyle } from 'pages/page/PageStyle';
import { useContext, useEffect } from 'react';
import { CtxView } from 'session/ctxView';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    mapChildren?: JSX.Element;
    interactiveLayerIds?: string[];
    onMarkerSelected?: (markerId: string) => boolean;
    onMapClickedOverride?: () => boolean;
    dragRotate?: boolean;
    touchPitch?: boolean;
}

export const MapV2 = (props: Props): JSX.Element => {
    const ctxView = useContext(CtxView);
    const ctxMapV2 = useContext(CtxMapV2);
    const actions = useActions();
    const mapQueryParams = useViewMapQueryParams();

    useUrlGuard(ctxView.map.worldMapId, mapQueryParams.language);
    useEffect(() => {
        ctxView.setCustomPageConfig({
            markerDetailStyle: 'MapV2'
        });
    }, []);

    const markers = useMarkers();

    const visibleStoryIds = uniq(
        markers.all.filter((e) => e.visible).flatMap((e) => e.storyIds ?? [])
    );

    useEffect(() => {
        document.getElementsByTagName('html')[0].classList.add('no-scroll');
        return () => {
            document.getElementsByTagName('html')[0].classList.remove('no-scroll');
        };
    }, [ctxMapV2.isWide]);

    // mapbox://styles/lukas-pasta/cm1qc9z8400tc01pi40dtbseq?optimized=true

    return (
        <S_MapV2 className={props.className}>
            <MapV2Categories />
            {!ctxMapV2.features.isListEnabled && ctxMapV2.features.isLanguageSwitchEnabled && (
                <S_MapV2LanguageSwitch
                    $isWide={ctxMapV2.isWide}
                    $withGeolocate={ctxView.map.allowGeolocation}
                />
            )}
            {ctxMapV2.isIframe && <MapV2IframeBar />}
            <CustomMap
                disableSimpleGestures={false}
                overrideMapMarkers={markers.all}
                onMarkerSelectedOverride={({ markerId }) => {
                    actions.handleMarkerClick(markerId);
                }}
                onMarkerSelected={props.onMarkerSelected}
                interactiveLayerIds={props.interactiveLayerIds}
                onMarkersSelectedOverride={({ markerIds }) => {
                    actions.handleMarkersClick(markerIds);
                }}
                markersCustomConfig={{
                    standScale: 1.3
                }}
                mapProps={{
                    dragRotate: props.dragRotate,
                    touchPitch: props.touchPitch
                }}
                language={mapQueryParams.language}
                onMapClickedOverride={() => {
                    if (props.onMapClickedOverride) {
                        return props.onMapClickedOverride();
                    }
                    // Needs to return if the click was handled
                    return actions.handleMapClick();
                }}
                mapChildren={props.mapChildren}
            />
            <MapV2Modal visibleStoryIds={visibleStoryIds} />
        </S_MapV2>
    );
};

const S_MapV2 = styled(S_PageStyle)`
    display: flex;
    flex-direction: column;
    position: relative;
    .mapboxgl-marker {
        pointer-events: none !important;
        cursor: initial;
        &.zIndex1 {
            z-index: 1;
        }
        &.zIndex2 {
            z-index: 2;
        }
        &.zIndex3 {
            z-index: 3;
        }
        &.zIndexActive {
            z-index: 4;
        }
    }
`;

const S_MapV2LanguageSwitch = styled(MapV2LanguageSwitch)<{
    $isWide: boolean;
    $withGeolocate: boolean;
}>`
    position: absolute;
    z-index: 1;
    ${(props) => {
        if (!props.$withGeolocate) {
            return css`
                bottom: 12px;
                right: 7px;
            `;
        }

        if (props.$isWide) {
            return css`
                right: 20px;
                bottom: 64px;
            `;
        } else {
            return css`
                bottom: 64px;
                right: 7px;
            `;
        }
    }}
`;
